/**
 * SortableImageItem Component
 * Individual image item with drag-and-drop, edit, and delete functionality
 */

import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FiX, FiEdit2, FiSave, FiMove } from 'react-icons/fi';

// Image type that supports both new uploads (with File) and existing images (with imageUrl)
interface SortableImageData {
  id: string;
  file?: File;
  preview: string; // For new uploads: blob URL, for existing: imageUrl
  caption?: string;
  altText?: string;
}

interface SortableImageItemProps {
  id: string;
  image: SortableImageData;
  index: number;
  isEditing: boolean;
  onRemove: (id: string) => void;
  onEdit: () => void;
  onSaveMetadata: (id: string, field: 'caption' | 'altText', value: string) => void;
  onCancelEdit: () => void;
  disabled?: boolean;
}

const SortableImageItem: React.FC<SortableImageItemProps> = ({
  id,
  image,
  index,
  isEditing,
  onRemove,
  onEdit,
  onSaveMetadata,
  onCancelEdit,
  disabled
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const [localCaption, setLocalCaption] = useState(image.caption || '');
  const [localAltText, setLocalAltText] = useState(image.altText || '');

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  const handleSave = () => {
    onSaveMetadata(id, 'caption', localCaption);
    onSaveMetadata(id, 'altText', localAltText);
    onCancelEdit();
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative group border-2 border-gray-300 rounded-lg overflow-hidden bg-white"
    >
      {/* Image */}
      <div className="aspect-video bg-gray-100">
        <img
          src={image.preview}
          alt={image.altText || `Gallery image ${index + 1}`}
          className="w-full h-full object-cover"
        />
      </div>

      {/* Overlay Controls */}
      {!isEditing && (
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all">
          <div className="absolute inset-0 flex items-center justify-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
            {/* Drag Handle */}
            <button
              {...attributes}
              {...listeners}
              className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
              title="ลากเพื่อจัดลำดับ"
              disabled={disabled}
            >
              <FiMove className="w-5 h-5 text-gray-700" />
            </button>

            {/* Edit Button */}
            <button
              onClick={onEdit}
              className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
              title="แก้ไขคำอธิบาย"
              disabled={disabled}
            >
              <FiEdit2 className="w-5 h-5 text-blue-600" />
            </button>

            {/* Remove Button */}
            <button
              onClick={() => onRemove(id)}
              className="p-2 bg-white rounded-full shadow-lg hover:bg-red-50 transition-colors"
              title="ลบรูปภาพ"
              disabled={disabled}
            >
              <FiX className="w-5 h-5 text-red-600" />
            </button>
          </div>
        </div>
      )}

      {/* Edit Mode */}
      {isEditing && (
        <div className="absolute inset-0 bg-white p-3 overflow-y-auto">
          <div className="space-y-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                คำอธิบายภาพ
              </label>
              <input
                type="text"
                value={localCaption}
                onChange={(e) => setLocalCaption(e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="เช่น: สำนักงานใหญ่"
                maxLength={255}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Alt Text (SEO)
              </label>
              <input
                type="text"
                value={localAltText}
                onChange={(e) => setLocalAltText(e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="คำอธิบายสำหรับ SEO"
                maxLength={255}
              />
            </div>

            <div className="flex space-x-2">
              <button
                onClick={handleSave}
                className="flex-1 flex items-center justify-center space-x-1 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 transition-colors"
              >
                <FiSave className="w-3 h-3" />
                <span>บันทึก</span>
              </button>
              <button
                onClick={onCancelEdit}
                className="flex-1 px-3 py-1.5 border border-gray-300 text-gray-700 text-sm font-medium rounded hover:bg-gray-50 transition-colors"
              >
                ยกเลิก
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Order Badge */}
      <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded shadow-md">
        {index + 1}
      </div>
    </div>
  );
};

export default SortableImageItem;
